import React from 'react';
import { MapPin, Phone, Mail, Facebook, Instagram, Linkedin, Twitter } from 'lucide-react';

export const Footer = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-white">
      <div className="container mx-auto px-4 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div>
            <div className="mb-4" data-testid="footer-logo">
              <img
                src={`${process.env.PUBLIC_URL || ''}/acremitra-logo.png`}
                alt="Acre Mitra — Your Trusted Real Estate Partner"
                className="h-24 w-auto object-contain bg-white rounded-lg p-2"
                onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden'); }}
                data-testid="footer-logo-img"
              />
              {/* Text fallback shown only if the image 404s */}
              <div className="hidden flex-col leading-none">
                <span className="text-white font-extrabold text-xl tracking-tight">Acre Mitra</span>
                <span className="text-[9px] uppercase tracking-[0.22em] text-amber-400 font-bold mt-1">Your Trusted Real Estate Partner</span>
              </div>
            </div>
            <p className="text-slate-400 mb-4 leading-relaxed">
              Your trusted partner in real estate. We specialize in land buying, 
              selling, and reselling with complete transparency and integrity.
            </p>
            <div className="flex gap-3">
              <a href="https://www.facebook.com/share/1AvfrhxQH8/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-800 hover:bg-emerald-700 rounded-full flex items-center justify-center transition-colors duration-300">
                <Facebook size={18} />
              </a>
              <a href="https://www.instagram.com/acremitra" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-800 hover:bg-emerald-700 rounded-full flex items-center justify-center transition-colors duration-300">
                <Instagram size={18} />
              </a>
              <a href="https://www.linkedin.com/company/acremitra" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-800 hover:bg-emerald-700 rounded-full flex items-center justify-center transition-colors duration-300">
                <Linkedin size={18} />
              </a>
              <a href="https://twitter.com/acremitra" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-800 hover:bg-emerald-700 rounded-full flex items-center justify-center transition-colors duration-300">
                <Twitter size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-4">Quick Links</h3>
            <ul className="space-y-3">
              {['Home', 'About', 'Services', 'Projects', 'Testimonials', 'Contact'].map((item) => (
                <li key={item}>
                  <button 
                    onClick={() => scrollToSection(item.toLowerCase())}
                    className="text-slate-400 hover:text-emerald-400 transition-colors duration-200"
                  >
                    {item}
                  </button>
                </li>
              ))}
              <li>
                <a
                  href="https://banglarbhumi.gov.in/BanglarBhumi/Home.action"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-amber-300 hover:text-amber-200 font-semibold transition-colors duration-200 inline-flex items-center gap-1"
                  data-testid="footer-banglarbhumi-link"
                  title="Verify West Bengal government land records on Banglarbhumi (opens new tab)"
                >
                  Verify on Banglarbhumi <span aria-hidden="true">↗</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold mb-4">Our Services</h3>
            <ul className="space-y-3">
              {['Land Buying Consultation', 'Property Resale Services', 'Legal Documentation', 'Site Visit Assistance', 'Investment Advisory', 'Post-Sale Support'].map((service) => (
                <li key={service}>
                  <button
                    onClick={() => scrollToSection('services')}
                    className="text-slate-400 hover:text-emerald-400 transition-colors duration-200 text-left"
                  >
                    {service}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-4">Contact Us</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin size={20} className="text-emerald-400 flex-shrink-0 mt-1" />
                <div className="text-slate-400 text-sm space-y-2">
                  <p>
                    <strong className="text-white">Kolkata:</strong><br />
                    343/1, Nursery Bagan Road,<br />
                    Dum Dum, Kolkata - 700074
                  </p>
                  <p>
                    <strong className="text-white">Sodepur:</strong><br />
                    Sukchar Bazaar, Sodepur
                  </p>
                  <p>
                    <strong className="text-white">Birbhum:</strong><br />
                    Station More, Mallarpur - 731216
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={20} className="text-emerald-400 flex-shrink-0" />
                <div className="text-slate-400 text-sm">
                  <a href="tel:+913331944955" className="hover:text-emerald-400 transition-colors block">
                    +91 33 3194 4955
                  </a>
                  <a href="tel:+918597310624" className="hover:text-emerald-400 transition-colors block">
                    +91 85973 10624
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={20} className="text-emerald-400 flex-shrink-0" />
                <a href="mailto:info@acremitra.com" className="text-slate-400 text-sm hover:text-emerald-400 transition-colors">
                  info@acremitra.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-slate-400 text-sm">
            <p>© {currentYear} Acre Mitra. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="/support" className="hover:text-emerald-400 transition-colors" data-testid="footer-support-link">Customer Support</a>
              <a href="#" className="hover:text-emerald-400 transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-emerald-400 transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-emerald-400 transition-colors">Cookie Policy</a>
              <a href="/crm-download" className="hover:text-emerald-400 transition-colors" data-testid="footer-crm-app">Employee CRM App</a>
              <a href="/hrm-download" className="hover:text-emerald-400 transition-colors" data-testid="footer-hrm-app">Employee HRM App</a>
              <a href="/partner-progress" className="hover:text-emerald-400 transition-colors" data-testid="footer-partner-progress">Partner Progress</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
